/* FloodRiskPage — slide 6 (Pro-gated).
 *
 * Surfaces GloFAS-driven river-discharge forecasts for the user's nearest
 * curated Indian river. Designed for monsoon-season decision making —
 * "is my river going to flood my village in the next 7 days?"
 *
 * Sections:
 *   1. River chip + "X km away" distance pill
 *   2. Hero: current discharge + severity band gauge
 *   3. "Next 7 days" peak vs baseline ratio
 *   4. 21-day discharge sparkline with ensemble band (p25-p75) shaded
 *   5. GloFAS / Open-Meteo attribution footer
 */
import { useMemo } from "react";
import { motion } from "framer-motion";
import { Drop, Waves, ArrowUpRight, Info, Warning, CheckCircle } from "@phosphor-icons/react";
import LocationBar from "@/components/v2/LocationBar";
import useFloodRisk from "@/hooks/useFloodRisk";

const SEVERITY = {
  severe:   { color: "#dc2626", soft: "#fee2e2", label: "SEVERE" },
  warning:  { color: "#ea580c", soft: "#ffedd5", label: "WATCH" },
  elevated: { color: "#d97706", soft: "#fef3c7", label: "ELEVATED" },
  normal:   { color: "#10b981", soft: "#d1fae5", label: "NORMAL" },
  low:      { color: "#0891b2", soft: "#cffafe", label: "LOW FLOW" },
  unknown:  { color: "#94a3b8", soft: "#f1f5f9", label: "—" },
};

function fmtDischarge(v) {
  if (v == null) return "—";
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  if (v >= 100) return `${Math.round(v)}`;
  return `${v.toFixed(1)}`;
}

export default function FloodRiskPage({ location, onSelectLocation, theme }) {
  const { data, status, error } = useFloodRisk(location);

  return (
    <div
      className="h-full w-full flex flex-col px-5 pt-2 pb-24 overflow-y-auto"
      data-testid="page-flood"
      style={{ color: theme.textPrimary, scrollbarWidth: "none" }}
    >
      <div className="shrink-0">
        <LocationBar
          location={location}
          onSelect={onSelectLocation}
          theme={theme}
        />
      </div>

      {status === "loading" && <FloodSkeleton theme={theme} />}
      {status === "error" && (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-2 px-6">
          <Warning size={28} weight="duotone" style={{ color: theme.accent }} />
          <p className="text-[14px] font-semibold" style={{ color: theme.textPrimary }}>
            Couldn't load flood data
          </p>
          <p className="text-[12px]" style={{ color: theme.textMuted }}>
            {error || "Try again in a minute."}
          </p>
        </div>
      )}
      {status === "ready" && data && <FloodBody data={data} theme={theme} location={location} />}
    </div>
  );
}

/** Friendly "no major river nearby" state for inland cities like Hyderabad,
 * Bengaluru, etc. The GloFAS sub-grid pixel still returns a discharge value
 * for such locations, but it isn't a real river — it's a tiny rivulet at
 * best. Showing a scary red "SEVERE" pill there is misleading; show a calm
 * "All clear" message instead. */
function FloodAllClear({ theme, location }) {
  return (
    <div
      className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3 mt-6"
      data-testid="flood-all-clear"
    >
      <div
        className="rounded-full flex items-center justify-center"
        style={{
          width: 84, height: 84,
          background: "rgba(16,185,129,0.12)",
          border: "2px solid rgba(16,185,129,0.4)",
        }}
      >
        <CheckCircle size={44} weight="duotone" color="#10b981" />
      </div>
      <h3
        className="font-display tracking-tight"
        style={{ fontSize: 22, fontWeight: 700, color: theme.textPrimary, letterSpacing: "-0.02em" }}
      >
        No flood risk near {location?.name || "you"}
      </h3>
      <p
        className="text-[12.5px] leading-snug max-w-[34ch]"
        style={{ color: theme.textSecondary }}
      >
        There's no major river within roughly 80 km of your location, so flood
        watches don't apply here. We'll show real-time river levels if you
        travel near one of India's major rivers.
      </p>
      <div
        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.16em] font-bold"
        style={{
          background: "rgba(16,185,129,0.10)",
          color: "#047857",
          border: "1px solid rgba(16,185,129,0.3)",
        }}
        data-testid="flood-all-clear-pill"
      >
        All clear
      </div>
    </div>
  );
}

function FloodBody({ data, theme, location }) {
  // No curated river within ~80 km → don't surface the GloFAS sub-grid noise
  // as a flood watch. Show a calm "All clear" state instead.
  if (!data.river || !data.river.name) {
    return <FloodAllClear theme={theme} location={location} />;
  }
  const sev = SEVERITY[data.severity?.level] || SEVERITY.unknown;
  const series = data.series || [];
  const todayIdx = series.findIndex((s) => s.is_forecast);

  return (
    <div className="flex-1 flex flex-col gap-3 mt-3 min-h-0">
      {/* Section: river identity */}
      <div
        className="text-[10px] font-bold uppercase tracking-[0.2em]"
        style={{ color: theme.textMuted }}
      >
        River level today
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-2xl px-4 py-3.5"
        style={{
          background: theme.surface,
          border: `1px solid ${theme.surfaceBorder}`,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
        data-testid="flood-river-card"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <Waves size={16} weight="duotone" style={{ color: theme.accent }} />
            <span
              className="font-display font-semibold tracking-tight truncate"
              style={{ fontSize: 18, color: theme.textPrimary }}
              data-testid="flood-river-name"
            >
              {data.river?.name || "Regional waterway"}
            </span>
          </div>
          {data.river?.distance_km != null && data.river.distance_km > 0.5 && (
            <span
              className="text-[10px] font-medium uppercase tracking-wider shrink-0"
              style={{ color: theme.textMuted }}
            >
              {data.river.distance_km.toFixed(1)} km away
            </span>
          )}
        </div>

        {/* Severity gauge */}
        <div className="mt-1">
          <div className="flex items-baseline justify-between">
            <div>
              <span
                className="font-display tabular-nums"
                style={{ fontSize: 38, fontWeight: 700, color: sev.color, letterSpacing: "-0.02em" }}
                data-testid="flood-current-discharge"
              >
                {fmtDischarge(data.current_m3s)}
              </span>
              <span className="ml-1 text-[12px]" style={{ color: theme.textMuted }}>
                m³/s
              </span>
            </div>
            <div
              className="px-2 py-1 rounded-full text-[10px] font-bold tracking-wider"
              style={{ background: sev.soft, color: sev.color }}
              data-testid="flood-severity-pill"
            >
              {sev.label}
            </div>
          </div>
          <div
            className="mt-2 h-2 rounded-full overflow-hidden"
            style={{ background: theme.chip }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.max(8, (data.severity?.progress || 0) * 100)}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-full rounded-full"
              style={{ background: sev.color }}
              data-testid="flood-severity-bar"
            />
          </div>
          <p
            className="mt-2 text-[11.5px] leading-snug"
            style={{ color: theme.textSecondary }}
          >
            {data.severity?.label}
          </p>
        </div>
      </motion.div>

      {/* Plain-English explainer */}
      <p
        className="text-[11px] leading-snug px-1 -mt-1"
        style={{ color: theme.textMuted }}
        data-testid="flood-explainer"
      >
        How much water is flowing through the {data.river.name}, and whether it's heading toward a flood.
      </p>

      {/* Next 7 vs baseline */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.08 }}
        className="grid grid-cols-2 gap-2"
      >
        <Stat
          theme={theme}
          label="Highest this week"
          value={fmtDischarge(data.next7?.peak_m3s)}
          unit="m³/s"
          accent={sev.color}
          icon={<ArrowUpRight size={11} weight="bold" />}
          testId="flood-next7-peak"
        />
        <Stat
          theme={theme}
          label="Typical for this season"
          value={fmtDischarge(data.baseline?.p75)}
          unit="m³/s"
          accent={theme.textSecondary}
          icon={<Drop size={11} weight="duotone" />}
          testId="flood-baseline-p75"
        />
      </motion.div>

      {/* Plain-English ratio explainer — only when ratio is meaningful */}
      {data.next7?.ratio_vs_p75 != null && data.next7.ratio_vs_p75 > 0 && (
        <p
          className="text-[11px] leading-snug -mt-1 px-1"
          style={{ color: theme.textSecondary }}
          data-testid="flood-ratio-blurb"
        >
          That's <strong style={{ color: sev.color }}>{data.next7.ratio_vs_p75.toFixed(1)}× the normal level</strong> for this time of year.
        </p>
      )}

      {/* Discharge chart */}
      <div
        className="text-[10px] font-bold uppercase tracking-[0.2em] mt-1"
        style={{ color: theme.textMuted }}
      >
        Flow trend · last week + 2 weeks ahead
      </div>
      <DischargeChart series={series} todayIdx={todayIdx} theme={theme} sevColor={sev.color} />

      <div className="flex-1" />

      {/* Footer */}
      <div
        className="flex items-center gap-1.5 mt-2 pt-1"
        style={{ color: theme.textMuted }}
      >
        <Info size={10} weight="duotone" />
        <span className="text-[9.5px] tracking-wide">
          Source: European Flood Awareness System · 5 km resolution
        </span>
      </div>
    </div>
  );
}

function DischargeChart({ series, todayIdx, theme, sevColor }) {
  const W = 360;
  const H = 132;
  const PADX = 8;
  const PADY = 12;

  const numbers = useMemo(() => {
    return series.flatMap((s) =>
      [s.discharge, s.mean, s.max, s.min, s.p25, s.p75].filter((v) => v != null),
    );
  }, [series]);

  if (!numbers.length) {
    return (
      <div
        className="rounded-2xl flex items-center justify-center text-[12px]"
        style={{
          height: H,
          background: theme.surface,
          border: `1px solid ${theme.surfaceBorder}`,
          color: theme.textMuted,
        }}
      >
        No discharge readings
      </div>
    );
  }

  const minV = Math.min(...numbers) * 0.92;
  const maxV = Math.max(...numbers) * 1.05;
  const range = maxV - minV || 1;
  const innerW = W - PADX * 2;
  const innerH = H - PADY * 2;
  const stepX = series.length > 1 ? innerW / (series.length - 1) : 0;

  const xAt = (i) => PADX + i * stepX;
  const yAt = (v) => PADY + (1 - (v - minV) / range) * innerH;

  // Ensemble band path (p25 lower, p75 upper) — only for forecast portion
  const bandTop = [];
  const bandBottom = [];
  series.forEach((s, i) => {
    if (s.is_forecast && s.p25 != null && s.p75 != null) {
      bandTop.push(`${i === 0 ? "M" : "L"}${xAt(i)},${yAt(s.p75)}`);
      bandBottom.push(`L${xAt(i)},${yAt(s.p25)}`);
    }
  });
  const bandPath = bandTop.length
    ? `${bandTop.join(" ")} ${bandBottom.reverse().join(" ")} Z`
    : "";

  // Main discharge line
  const linePoints = series
    .map((s, i) => (s.discharge != null ? `${i === 0 ? "M" : "L"}${xAt(i)},${yAt(s.discharge)}` : ""))
    .filter(Boolean)
    .join(" ");

  const todayX = todayIdx >= 0 ? xAt(todayIdx) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.16 }}
      className="rounded-2xl overflow-hidden"
      style={{
        background: theme.surface,
        border: `1px solid ${theme.surfaceBorder}`,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
      data-testid="flood-chart"
    >
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" preserveAspectRatio="none">
        {/* Ensemble band */}
        {bandPath && (
          <path
            d={bandPath}
            fill={sevColor}
            opacity={0.18}
          />
        )}
        {/* Today vertical marker */}
        {todayX != null && (
          <line
            x1={todayX}
            x2={todayX}
            y1={PADY}
            y2={H - PADY}
            stroke={theme.textMuted}
            strokeWidth={1}
            strokeDasharray="3 3"
            opacity={0.6}
          />
        )}
        {/* Discharge line */}
        <path
          d={linePoints}
          fill="none"
          stroke={sevColor}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Today dot */}
        {todayX != null && series[todayIdx]?.discharge != null && (
          <circle
            cx={todayX}
            cy={yAt(series[todayIdx].discharge)}
            r={3.5}
            fill={sevColor}
          />
        )}
      </svg>
      <div className="flex items-center justify-between px-3 pb-2 pt-1">
        <span className="text-[9.5px] uppercase tracking-wider" style={{ color: theme.textMuted }}>
          7d ago
        </span>
        <span className="text-[9.5px] uppercase tracking-wider font-bold" style={{ color: theme.textSecondary }}>
          today
        </span>
        <span className="text-[9.5px] uppercase tracking-wider" style={{ color: theme.textMuted }}>
          14d ahead
        </span>
      </div>
    </motion.div>
  );
}

function Stat({ theme, label, value, unit, accent, icon, testId }) {
  return (
    <div
      className="rounded-2xl px-3 py-2.5"
      style={{
        background: theme.surface,
        border: `1px solid ${theme.surfaceBorder}`,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
      data-testid={testId}
    >
      <div className="flex items-center gap-1 mb-0.5">
        <span style={{ color: accent }}>{icon}</span>
        <span
          className="text-[8.5px] font-bold uppercase tracking-[0.16em]"
          style={{ color: theme.textMuted }}
        >
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span
          className="font-display tabular-nums"
          style={{ fontSize: 20, fontWeight: 700, color: theme.textPrimary, letterSpacing: "-0.01em" }}
        >
          {value}
        </span>
        <span className="text-[10px]" style={{ color: theme.textMuted }}>
          {unit}
        </span>
      </div>
    </div>
  );
}

function FloodSkeleton({ theme }) {
  return (
    <div className="flex-1 flex flex-col gap-3 mt-3">
      <div
        className="rounded-2xl animate-pulse"
        style={{ background: theme.surface, height: 130 }}
      />
      <div className="grid grid-cols-2 gap-2">
        <div
          className="rounded-2xl animate-pulse"
          style={{ background: theme.surface, height: 70 }}
        />
        <div
          className="rounded-2xl animate-pulse"
          style={{ background: theme.surface, height: 70 }}
        />
      </div>
      <div
        className="rounded-2xl animate-pulse"
        style={{ background: theme.surface, height: 150 }}
      />
    </div>
  );
}
